import { Subject } from 'rxjs';
import {
  JitsiConferenceEvents,
  JitsiConnectionEvents,
  JitsiGenericEvent,
} from 'nexgen-types/lib-jitsi';
import {
  MeetingTrackType,
  MeetingConnectionState,
  MeetingRoomState,
  MeetingTrackAction,
  MeetingParticipantAction,
} from '../../interfaces/meeting-service.interface';
import { JitsiMeetingEvent } from '../../services/jitsi-meeting.service';

export class JitsiEventMapper {
  private eventSubject: Subject<JitsiMeetingEvent>;
  private getTrackType: (track: any) => MeetingTrackType;
  private connectionState: MeetingConnectionState;
  private roomState: MeetingRoomState;
  private roomName: string;
  private debug: boolean;

  constructor(
    eventSubject: Subject<JitsiMeetingEvent>,
    trackTypeGetter: (track: any) => MeetingTrackType,
    debug: boolean = false
  ) {
    this.eventSubject = eventSubject;
    this.getTrackType = trackTypeGetter;
    this.connectionState = MeetingConnectionState.DISCONNECTED;
    this.roomState = MeetingRoomState.LEFT;
    this.roomName = '';
    this.debug = debug;
  }

  // State setters
  setConnectionState(state: MeetingConnectionState): void {
    this.connectionState = state;
  }

  setRoomState(state: MeetingRoomState): void {
    this.roomState = state;
  }

  setRoomName(name: string): void {
    this.roomName = name;
  }

  // Get current states
  getConnectionState(): MeetingConnectionState {
    return this.connectionState;
  }

  getRoomState(): MeetingRoomState {
    return this.roomState;
  }

  getRoomName(): string {
    return this.roomName;
  }

  // Toggle debug mode
  setDebug(enabled: boolean): void {
    this.debug = enabled;
  }

  // --- Connection event handlers ---
  handleConnectionEstablished = (): void => {
    this.connectionState = MeetingConnectionState.CONNECTED;
    this.emitEvent(this.createConnectionEstablished());
  };

  handleConnectionFailed = (error: any): void => {
    this.connectionState = MeetingConnectionState.FAILED;
    this.emitEvent(this.createConnectionFailed(error));
  };

  handleConnectionDisconnected = (): void => {
    this.connectionState = MeetingConnectionState.DISCONNECTED;
    this.emitEvent(this.createConnectionDisconnected());
  };

  // --- Conference event handlers ---
  handleTrackAdded = (track: any): void => {
    const trackType = this.getTrackType(track);
    this.emitEvent(this.createTrackAdded(track, trackType));
  };

  handleTrackRemoved = (track: any): void => {
    const trackType = this.getTrackType(track);
    this.emitEvent(this.createTrackRemoved(track, trackType));
  };

  handleTrackMuteChanged = (track: any): void => {
    const trackType = this.getTrackType(track);
    const isMuted = track.isMuted();
    this.emitEvent(this.createTrackMuteChanged(track, trackType, isMuted));
  };

  handlePrivateMessageReceived = (participantId: any, data: any): void => {
    this.emitEvent(this.createPrivateMessagePayload(participantId, data));
  };

  handleConferenceJoined = (): void => {
    this.roomState = MeetingRoomState.JOINED;
    this.emitEvent(this.createConferenceJoined());
  };

  handleUserJoined = (id: string, user: any): void => {
    this.emitEvent(this.createUserJoined(id, user));
  };

  handleUserLeft = (id: string, user: any): void => {
    this.emitEvent(this.createUserLeft(id, user));
  };

  handleConferenceError = (error: any): void => {
    this.roomState = MeetingRoomState.ERROR;
    this.emitEvent(this.createConferenceError(error));
  };

  handleConferenceJoinError = (error: any): void => {
    console.log('ERROR JOINING', error);
    this.roomState = MeetingRoomState.ERROR;
    this.emitEvent(this.createConferenceError(error));
  };

  handleRecorderStateChanged = (data: any): void => {
    this.emitEvent(this.createRecorderStateChanged(data));
  };

  handleBreakoutRoomStateChanged = (data: any): void => {
    this.emitEvent(this.createBreakoutRoomsUpdate(data));
  };

  handleMoveToBreakoutRoom = (data: any): void => {
    this.emitEvent(
      this.constructGenericEvent(JitsiConferenceEvents.BREAKOUT_ROOMS_MOVE_TO_ROOM, data)
    );
  };

  handleTrackError = (error: any): void => {
    const trackType = error?.track ? this.getTrackType(error.track) : MeetingTrackType.VIDEO;
    this.emitEvent(this.createTrackError(trackType, error));
  };

  private emitEvent(event: JitsiMeetingEvent): void {
    if (this.debug) {
      console.log(`[- JitsiEventMapper -] Event emitted:`, {
        type: event.type,
        timestamp: new Date().toISOString(),
        data: event,
      });
    }
    this.eventSubject.next(event);
  }

  // --- Event creation methods ---

  // Connection events

  constructGenericEvent(type: any, payload: any) {
    return { type, payload };
  }

  createConnectionEstablished(): JitsiMeetingEvent {
    return {
      type: JitsiConnectionEvents.CONNECTION_ESTABLISHED,
      state: this.connectionState,
    };
  }

  createConnectionFailed(error: any): JitsiMeetingEvent {
    return {
      type: JitsiConnectionEvents.CONNECTION_FAILED,
      state: this.connectionState,
      payload: error,
    };
  }

  createConnectionDisconnected(): JitsiMeetingEvent {
    return {
      type: JitsiConnectionEvents.CONNECTION_DISCONNECTED,
      state: this.connectionState,
    };
  }

  createRecorderStateChanged = (data: any): JitsiMeetingEvent => {
    return {
      type: JitsiConferenceEvents.RECORDER_STATE_CHANGED,
      payload: data,
    };
  };

  createPrivateMessagePayload = (participantId: any, data: any): JitsiMeetingEvent => {
    return {
      participantId,
      type: JitsiConferenceEvents.PRIVATE_MESSAGE_RECEIVED,
      payload: JSON.parse(data),
    };
  };

  createBreakoutRoomsUpdate = (data: any): JitsiMeetingEvent => {
    return {
      type: JitsiConferenceEvents.BREAKOUT_ROOMS_UPDATED,
      payload: data,
    };
  };

  // Conference events
  createTrackAdded(track: any, trackType: MeetingTrackType): JitsiMeetingEvent {
    return {
      type: JitsiConferenceEvents.TRACK_ADDED,
      trackType,
      action: MeetingTrackAction.ADDED,
      payload: track,
      participantId: track.getParticipantId(),
    };
  }

  createTrackMuteChanged = (track: any, trackType: string, isMuted: boolean): any => {
    return {
      type: JitsiConferenceEvents.TRACK_MUTE_CHANGED,
      payload: track,
      trackType,
      isMuted,
      timestamp: Date.now(),
    };
  };

  createTrackRemoved(track: any, trackType: MeetingTrackType): JitsiMeetingEvent {
    return {
      type: JitsiConferenceEvents.TRACK_REMOVED,
      trackType,
      action: MeetingTrackAction.REMOVED,
      payload: track,
    };
  }

  createConferenceJoined(): JitsiMeetingEvent {
    return {
      type: JitsiConferenceEvents.CONFERENCE_JOINED,
      state: this.roomState,
      roomName: this.roomName,
    };
  }

  createConferenceError(error: any): JitsiMeetingEvent {
    return {
      type: JitsiConferenceEvents.CONFERENCE_ERROR,
      state: this.roomState,
      roomName: this.roomName,
      payload: error,
    };
  }

  createConferenceLeft(error?: any): JitsiMeetingEvent {
    return {
      type: JitsiConferenceEvents.CONFERENCE_LEFT,
      state: this.roomState,
      roomName: this.roomName,
      ...(error && { payload: error }),
    };
  }

  createUserJoined(id: string, user: any): JitsiMeetingEvent {
    return {
      type: JitsiConferenceEvents.USER_JOINED,
      action: MeetingParticipantAction.JOINED,
      participantId: id,
      payload: user,
    };
  }

  createUserLeft(id: string, user: any): JitsiMeetingEvent {
    return {
      type: JitsiConferenceEvents.USER_LEFT,
      action: MeetingParticipantAction.LEFT,
      participantId: id,
      payload: user,
    };
  }

  // Track events
  createTrackMuted(type: MeetingTrackType, participantId: string, track: any): JitsiMeetingEvent {
    return {
      type:
        type === MeetingTrackType.AUDIO
          ? JitsiGenericEvent.AUDIO_MUTED
          : JitsiGenericEvent.VIDEO_MUTED,
      trackType: type,
      action: MeetingTrackAction.MUTED,
      participantId,
      track,
    };
  }

  createTrackUnmuted(type: MeetingTrackType, participantId: string, track: any): JitsiMeetingEvent {
    return {
      type:
        type === MeetingTrackType.AUDIO
          ? JitsiGenericEvent.AUDIO_UNMUTED
          : JitsiGenericEvent.VIDEO_UNMUTED,
      trackType: type,
      action: MeetingTrackAction.UNMUTED,
      participantId,
      track,
    };
  }

  createTrackError(type: MeetingTrackType, error: any): JitsiMeetingEvent {
    return {
      type:
        type === MeetingTrackType.AUDIO
          ? JitsiGenericEvent.AUDIO_TOGGLE_ERROR
          : JitsiGenericEvent.VIDEO_TOGGLE_ERROR,
      trackType: type,
      action: MeetingTrackAction.ERROR,
      payload: error,
    };
  }

  createParticipantMuted(participantId: string): JitsiMeetingEvent {
    return {
      type: JitsiGenericEvent.PARTICIPANT_MUTED,
      action: MeetingParticipantAction.MUTED,
      participantId,
    };
  }

  createParticipantKicked(participantId: string, reason: string): JitsiMeetingEvent {
    return {
      type: JitsiConferenceEvents.PARTICIPANT_KICKED,
      action: MeetingParticipantAction.KICKED,
      participantId,
      payload: { reason },
    };
  }

  createTrackAttached(trackId: string, container: HTMLElement): JitsiMeetingEvent {
    return {
      type: JitsiGenericEvent.TRACK_ATTACHED,
      payload: { trackId, container },
    };
  }

  createTrackDetached(trackId: string, container: HTMLElement): JitsiMeetingEvent {
    return {
      type: JitsiGenericEvent.TRACK_DETACHED,
      payload: { trackId, container },
    };
  }

  createLocalTracksError(error: any): JitsiMeetingEvent {
    return {
      type: JitsiGenericEvent.LOCAL_TRACKS_ERROR,
      payload: error,
    };
  }
}
